import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { chatWithAIStream, fetchPublishedModels, fetchPublishedModelDetail, manageModelInteractions } from '../lib/api';
import { 
  BookOpen, MessageCircle, Edit3, Search, Filter, ArrowLeft, Send, X, Minimize2, Maximize2,
  ChevronRight, Grid, List, Brain, Lightbulb, Target, Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import UnifiedNavigation from '../components/common/UnifiedNavigation';
import BreadcrumbNavigation from '../components/common/BreadcrumbNavigation';
import { ResponsiveLogo } from '../components/common/Logo';
import SEOHead from '../components/common/SEOHead';
import SkeletonLoader from '../components/common/SkeletonLoader';
import { processModelContent } from '../utils/htmlContentProcessor';
import '../styles/modelContent.css';

interface Model {
  id: string;
  name: string;
  description: string;
  content: string;
  tags: string[];
  created_at: string;
}

const ModelsList: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [models, setModels] = useState<Model[]>([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchModels();
  }, []);

  // 更新URL参数
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedTag) params.set('tag', selectedTag);
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedTag, setSearchParams]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await fetchPublishedModels({
        search: searchTerm || undefined,
        tag: selectedTag || undefined
      });
      if (response.success) {
        setModels(response.data?.models || []);
      } else {
        throw new Error(response.error || '获取模型失败');
      }
    } catch (error) {
      console.error('获取模型失败:', error);
      setError(error instanceof Error ? error.message : '获取模型失败');
      toast.error('获取模型失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取所有标签
  const allTags = [...new Set(models.flatMap(model => model.tags))];

  // 过滤模型
  const filteredModels = models.filter(model => {
    const matchesSearch = !searchTerm || 
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTag = !selectedTag || model.tags.includes(selectedTag);
    
    return matchesSearch && matchesTag;
  });

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-4">无法加载模型数据，请稍后重试。</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="模型工厂 - LeapWard深度思考平台"
        description="探索和学习各种思维模型，提升认知能力和决策水平。掌握经典思维框架，成为更好的思考者。"
        keywords="思维模型,认知框架,决策工具,LeapWard,批判性思维,逻辑思维,创新思维"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* 顶部导航栏 */}
        <UnifiedNavigation currentPage="models" />

        {/* 页面内容区域 */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 面包屑导航 */}
          <BreadcrumbNavigation 
            items={[
              { name: '模型工厂', current: true }
            ]}
            className="mb-6"
          />

          {/* 页面标题和统计 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">思维模型工厂</h1>
              <p className="text-gray-600">
                {loading ? '加载中...' : `共 ${filteredModels.length} 个思维模型`}
              </p>
            </div>
            
            {/* 视图切换 */}
            <div className="flex items-center space-x-2 mt-4 sm:mt-0">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-purple-100 text-purple-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-purple-100 text-purple-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 搜索和筛选栏 */}
          <div className="bg-white rounded-lg shadow-sm border mb-8 p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* 搜索框 */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="搜索思维模型..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* 标签筛选 */}
              <div className="sm:w-48">
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">所有标签</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 模型列表 */}
          {loading ? (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }>
              {[...Array(6)].map((_, index) => (
                <SkeletonLoader key={index} className="h-64" />
              ))}
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 text-6xl mb-4">🧠</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || selectedTag ? '未找到匹配的模型' : '暂无思维模型'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || selectedTag
                  ? '请尝试调整搜索条件或标签筛选'
                  : '精彩的思维模型即将上线，敬请期待'
                }
              </p>
              {(searchTerm || selectedTag) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTag('');
                  }}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  清除筛选条件
                </button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }>
              {filteredModels.map((model) => (
                <Link
                  key={model.id}
                  to={`/models/${model.id}`}
                  className={`bg-white rounded-lg shadow-sm hover:shadow-xl hover:border-purple-300 hover:-translate-y-1 transition-all duration-300 border block group cursor-pointer ${
                    viewMode === 'list' ? 'p-6' : 'overflow-hidden'
                  }`}
                >
                  {viewMode === 'grid' ? (
                    // 网格视图
                    <>
                      {/* 顶部渐变条 */}
                      <div className="h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500"></div>
                      
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-blue-600 rounded flex items-center justify-center">
                              <Brain className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm text-purple-600 font-medium">
                              思维模型
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {new Date(model.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors duration-200">
                          {model.name}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {model.description}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                          <div className="flex flex-wrap gap-1">
                            {model.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                            {model.tags.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                +{model.tags.length - 3}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Lightbulb className="w-3 h-3" />
                            <span>学习</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // 列表视图
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <Brain className="w-8 h-8 text-white" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-purple-600 font-medium">
                              思维模型
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {new Date(model.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors duration-200">
                          {model.name}
                        </h3>
                        
                        <p className="text-gray-600 mb-3 line-clamp-2">
                          {model.description}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex flex-wrap gap-1">
                            {model.tags.slice(0, 4).map(tag => (
                              <span key={tag} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                            {model.tags.length > 4 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                +{model.tags.length - 4}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-500">
                            <Lightbulb className="w-4 h-4 mr-1" />
                            学习模型
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        {!user && filteredModels.length > 0 && (
          <div className="bg-purple-50 border-t">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  想要深度学习思维模型？
                </h3>
                <p className="text-gray-600 mb-4">
                  注册账号，解锁AI互动学习、个人学习记录和进阶思维训练功能
                </p>
                <button
                  onClick={() => navigate('/', { state: { scrollTo: 'hero' } })}
                  className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  立即注册
                  <Brain className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ModelDetail组件保持独立，有自己的导航栏配置
const ModelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [model, setModel] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(400);


  useEffect(() => {
    if (id) {
      fetchModel();
      fetchChatHistory();
    }
  }, [id]);

  const fetchModel = async () => {
    try {
      setLoading(true);
      const response = await fetchPublishedModelDetail(id!);
      if (response.success) {
        setModel(response.data?.model);
      } else {
        throw new Error(response.error || '获取模型详情失败');
      }
    } catch (error) {
      console.error('获取模型详情失败:', error);
      setError(error instanceof Error ? error.message : '获取模型详情失败');
      toast.error('获取模型详情失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async () => {
    try {
      const response = await manageModelInteractions({
        action: 'get',
        model_id: id!
      });
      if (response.success) {
        setChatMessages(response.data?.interactions || []);
      }
    } catch (error) {
      console.error('获取聊天历史失败:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending || !model) return;

    const message = newMessage.trim();
    setNewMessage('');
    setIsSending(true);

    // 添加用户消息
    const userMessage = {
      id: Date.now().toString(),
      content: message,
      type: 'user',
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, userMessage]);

    try {
      // 保存用户消息
      await manageModelInteractions({
        action: 'add',
        model_id: id!,
        user_message: message,
        ai_response: '' // 暂时为空，稍后更新
      });

      // 构建AI提示
      const prompt = `作为思维模型专家，基于以下模型内容回答用户问题：

模型名称：${model.name}
模型描述：${model.description}
模型内容：${model.content}

用户问题：${message}

请基于这个思维模型的内容，提供准确、有帮助的回答。如果问题超出了模型范围，请礼貌地说明并提供相关建议。`;

      let aiResponse = '';
      const aiMessageId = (Date.now() + 1).toString();
      
      // 添加AI消息占位符
      const aiMessage = {
        id: aiMessageId,
        content: '',
        type: 'ai',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, aiMessage]);

      // 流式接收AI回复
      await chatWithAIStream(
        prompt,
        (chunk) => {
          aiResponse += chunk;
          setChatMessages(prev => 
            prev.map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, content: aiResponse }
                : msg
            )
          );
        }
      );

      // 保存完整的AI回复
      await manageModelInteractions({
        action: 'add',
        model_id: id!,
        user_message: message,
        ai_response: aiResponse
      });

    } catch (error) {
      console.error('发送消息失败:', error);
      toast.error('发送消息失败');
      
      // 移除失败的消息
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在加载模型详情...</p>
        </div>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-4">{error || '模型不存在'}</p>
          <button
            onClick={() => navigate('/models')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            返回模型列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {/* Main Content */}
      <div className="flex-1 transition-all duration-300" style={{ marginRight: showChat ? `${sidebarWidth}px` : '0' }}>
        {/* 顶部导航栏 */}
        <UnifiedNavigation currentPage="models" />
        
        {/* AI聊天工具栏 - 仅在用户登录时显示 */}
        {user && (
          <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowChat(!showChat)}
                  className={`flex items-center px-3 py-2 rounded-lg transition-colors ${
                    showChat 
                      ? 'bg-purple-100 text-purple-600' 
                      : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                  title="AI智能问答"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  AI问答
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 页面内容 */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 面包屑导航 */}
          <BreadcrumbNavigation 
            items={[
              { name: '模型工厂', href: '/models' },
              { name: model.name, current: true }
            ]}
            className="mb-6"
          />

          {/* 返回按钮 */}
          <button
            onClick={() => navigate('/models')}
            className="flex items-center text-purple-600 hover:text-purple-700 mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            返回模型列表
          </button>

          {/* 模型详情卡片 */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-8">
            {/* 模型头部 */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-8 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold mb-2">{model.name}</h1>
                  <p className="text-purple-100 text-lg">{model.description}</p>
                </div>
                <div className="ml-6">
                  <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
              
              {/* 标签 */}
              <div className="flex flex-wrap gap-2 mt-4">
                {model.tags?.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 bg-white/20 text-white rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* 模型内容 */}
            <div className="px-6 py-8">
              <div className="prose prose-lg max-w-none">
                {(() => {
                  const { processedContent, contentType } = processModelContent(model.content);
                  
                  if (contentType === 'html') {
                    return (
                      <div 
                        dangerouslySetInnerHTML={{ __html: processedContent }}
                        className="model-html-content"
                      />
                    );
                  } else {
                    return <ReactMarkdown>{processedContent}</ReactMarkdown>;
                  }
                })()}
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-4 mb-8">
            {user && (
              <button
                onClick={() => setShowChat(true)}
                className="flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                AI智能问答
              </button>
            )}
            <button
              onClick={() => navigate('/models')}
              className="flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              浏览更多模型
            </button>
          </div>
        </div>
      </div>

      {/* AI聊天侧边栏 */}
      {showChat && user && (
        <div 
          className="fixed right-0 top-0 h-full bg-white border-l border-gray-200 shadow-xl z-40 flex flex-col"
          style={{ width: sidebarWidth }}
        >
          {/* 聊天头部 */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">AI助手</h3>
                <p className="text-sm text-gray-500">{model.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowChat(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* 聊天消息区域 */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>向AI助手提问关于这个思维模型的任何问题</p>
                  </div>
                ) : (
                  chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.type === 'user'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <ReactMarkdown className="text-sm">{message.content}</ReactMarkdown>
                      </div>
                    </div>
                  ))
                )}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg px-4 py-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 消息输入框 */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="输入你的问题..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    disabled={isSending}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || isSending}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Models: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  
  if (id) {
    return <ModelDetail />;
  }
  
  return <ModelsList />;
};

export default Models;